import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';
import ReactPlayer from 'react-player';

import BlurredBlobs from '../Common/BlurredBlobs';
import ContentfulImage from '../Common/ContentfulImage';
import { PrimaryLink } from '../Common/Links';
import RichTextConverter from '../Common/RichText/RichTextConverter';
import HeadingCtas from '../Generic/HeadingCtas';
import ContentContainer from '../PageSections/ContentContainer';
import * as styles from './OverlappingHero.module.scss';

const OverlappingHero = ({
  colorTheme,
  backgroundImage,
  hero,
  copy,
  headingSuper,
  headingSuperIcon,
  heading,
  ctaLabel,
  ctaOnClick,
  ctas,
  center,
  videoUrl,
  fullBleed,
  blobs,
  anchored,
}) => {
  const containerRef = useRef();

  let blobColors = null;
  switch (colorTheme) {
    case 'emerald':
      blobColors = { primary: '#07C799', secondary: '#9DFFE8' };
      break;
    case 'gold':
      blobColors = { primary: '#EE9528', secondary: '#FFD5A1' };
      break;
    case 'lilac':
      blobColors = { primary: '#9E36F1', secondary: '#E1BBFF' };
      break;
    case 'workforce':
      blobColors = { primary: '#FF708A', secondary: '#9E36F1' };
      break;
    default:
      break;
  }

  const hasHeroImage = hero || videoUrl;

  return (
    <div ref={containerRef} data-analytics-section={'hero-overlapping'}>
      {blobs && (
        <BlurredBlobs
          primaryColor={blobColors?.primary}
          secondaryColor={blobColors?.secondary}
          container={containerRef}
        />
      )}
      <ContentContainer
        wide
        className={[
          styles.padAdjust,
          center ? styles.noPad : '',
          fullBleed ? styles.bleed : '',
          backgroundImage ? styles.hasBackground : '',
        ].join(' ')}
        style={backgroundImage ? { backgroundImage: `url('${backgroundImage.file?.url}')` } : {}}
      >
        <div
          className={[
            styles.grid,
            center ? styles.center : '',
            styles.wrap,
            anchored ? styles.anchored : '',
            !hasHeroImage ? styles.widerText : '',
          ].join(' ')}
        >
          <div className={styles.copy} data-analytics-section={'hero-heading'}>
            {headingSuper ? (
              <>
                <h1 className={[styles.headingSuper, colorTheme ? styles[colorTheme] : ''].join(' ')}>
                  {headingSuperIcon && <ContentfulImage imageFragment={headingSuperIcon} />}
                  {headingSuper}
                </h1>
                <h2 className={styles.useH1}>{heading}</h2>
              </>
            ) : (
              <h1>{heading}</h1>
            )}
            <RichTextConverter copy={copy} className={styles.paragraph} />
            {!ctas && <PrimaryLink isButton label={ctaLabel} onClick={ctaOnClick} />}
            {ctas && <HeadingCtas ctas={ctas} className={[colorTheme ? styles[colorTheme] : ''].join(' ')} />}
          </div>
          {!videoUrl && hero && (
            <div className={styles.hero} data-analytics-component={'hero-image'}>
              {hero.file && (
                <ContentfulImage imageFragment={hero} fetchpriority={'high'} className={styles.heroImage} />
              )}
            </div>
          )}
          {videoUrl && (
            <div className={styles.grey} data-analytics-component={'hero-video'}>
              <div className={styles.videoAdjust}>
                <div className={styles.wistiaWrap}>
                  <div className={`wistia-wrapper-${videoUrl}`}>
                    <HydrationProvider>
                      <Client>
                        <ReactPlayer
                          controls={true}
                          className={`wistia-react-player-${videoUrl}`}
                          width={'100%'}
                          height="100%"
                          url={videoUrl}
                          config={{
                            wistia: {
                              options: {
                                smallPlayButton: true,
                              },
                            },
                          }}
                        />
                      </Client>
                    </HydrationProvider>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ContentContainer>
    </div>
  );
};

OverlappingHero.propTypes = {
  hero: PropTypes.object,
  copy: PropTypes.object,
  heading: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaOnClick: PropTypes.func,
};

export default OverlappingHero;
